/**
 * Notification helpers — link resolution + Thai relative time.
 *
 * Notification `link` values were authored for the vanilla hash-router
 * (`#/route`). This app is a pathname router (React Router), and some vanilla
 * route ids differ from the React nav ids. `resolveNotifLink` strips the hash
 * and remaps the known-divergent ids so clicking a notification lands on a real
 * page instead of bouncing to "/".
 */

/** Vanilla/legacy route segment → React nav id. Only divergent ones listed. */
const ROUTE_ALIASES: Record<string, string> = {
  assessments: 'evaluation', // assessment runs live under the evaluation page
  feedback360: 'evaluation',
  'tasks-kanban': 'tasks',
  idp: 'dev-plans', // IDP was consolidated into dev-plans
  onboarding: 'onboarding-journey',
  'talent-grid': 'career',
  'salary-structure': 'dashboard', // comp is read-only (PayPilot SoT) — no page here
};

/** Turn a notification `link` into a React Router pathname. */
export function resolveNotifLink(link?: string | null): string {
  if (!link) return '/dashboard';
  let p = link.trim().replace(/^#/, ''); // drop leading hash
  if (!p.startsWith('/')) p = `/${p}`;
  const [, seg = '', ...rest] = p.split('/');
  const mapped = ROUTE_ALIASES[seg];
  if (mapped) return `/${[mapped, ...rest].join('/')}`.replace(/\/$/, '') || `/${mapped}`;
  return p;
}

/** "เมื่อสักครู่ · 5 นาทีที่แล้ว · 3 ชม.ที่แล้ว · 2 วันที่แล้ว · 31 พ.ค." */
export function timeAgoTh(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return 'เมื่อสักครู่';
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} ชม.ที่แล้ว`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} วันที่แล้ว`;
  const d = new Date(iso);
  const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
}

/** Map a notification kind/severity hint to a left-border accent color. */
export function notifAccent(payload?: Record<string, unknown> | null): string {
  const sev = (payload?.severity as string) || '';
  if (sev === 'urgent' || sev === 'high') return '#dc2626'; // red
  if (sev === 'warn' || sev === 'medium') return '#d97706'; // amber
  return '#6366f1'; // brand indigo (default)
}
